/**
 * custom.js — MIGRATED FROM PRESTIGE 4
 *
 * Live's assets/custom.js held four independent IIFEs. Only one of them is
 * portable to Prestige 11.4.0; the rest are recorded here so nobody has to
 * re-derive why they were left behind.
 *
 *  1. Collapsible height fix — targeted `.Collapsible__Button[aria-expanded]`
 *     and its `.Collapsible__Inner` sibling. Neither class exists in 11.4.0
 *     (the only `Collapsible__` strings in this theme are comments describing
 *     live's markup); v11 uses native <details>, which sizes itself. Dead code
 *     here, so not ported.
 *
 *  2. Sticky add-to-cart bar — needed `#StickyAtc`, `#StickyAtcPanel`,
 *     `.ProductForm__AddToCart`, `.ProductForm__Option`, `.ColorSwatchList`
 *     and `#sidebar-cart`. None exist in 11.4.0. It early-returns on
 *     `if (!bar) return`, so copying it would be silently inert. Restoring the
 *     feature is a markup port, not a file copy — deliberately not attempted
 *     without a decision on whether the bar is still wanted.
 *
 *  3. Cart drawer "OTHERS ALSO BOUGHT" recommendations — LIVE IN THIS THEME.
 *
 *     Live's version needed `#CartRecommendations` and a
 *     `cart-recommendations-items` section, neither of which exists in 11.4.0,
 *     so none of live's JS was copied. The feature is instead built on v11's
 *     own <product-recommendations> element in sections/cart-drawer.liquid,
 *     which fetches /recommendations/products with intent="related".
 *
 *     It was briefly disabled (marker: "DISABLED custom cart upsell —
 *     2026-09-03") and has since been re-enabled, along with the 13 CSS rules
 *     in that section's <style> block that had been commented out with it.
 *     An earlier version of this comment still described it as not ported.
 *
 *     Two behaviours for it live further down THIS file, because v11's own
 *     component does not provide them:
 *       - the strip is re-fetched after a cart change. <cart-drawer> is moved
 *         to <body> while open, so theme.js's element cannot resolve its
 *         section id and the strip would silently vanish until the drawer was
 *         closed and reopened.
 *       - the accordion is collapsed after something is added from it, which
 *         is what live does.
 *
 *     Note it renders only when the curated "Complete with" list is empty —
 *     see the `cross_sell_products_count == 0` gate in cart-drawer.liquid.
 *
 *  4. Instagram feed image deferral — ported below verbatim. It is plain DOM
 *     code with no Prestige 4 dependency, and `#insta-feed` still exists in
 *     this theme (templates/index.json, the custom-liquid instafeed section).
 *
 * Note none of live's custom.js used jQuery, and none of it read the
 * `window.theme` globals — so neither had to be reintroduced for this file.
 */

/**
 * Instagram feed: defer offscreen images.
 *
 * The feed lives near the bottom of the home page but its images are injected at
 * runtime by a third-party app, straight from Instagram's CDN and at full
 * resolution (single images have measured over 1MB). Because the app writes the
 * <img> tags itself we can't set the attributes in Liquid, so they're marked
 * lazy/async as they arrive — the bytes are then only fetched if a visitor
 * actually scrolls down to the feed.
 */
(function () {
  var feed = document.getElementById('insta-feed');
  if (!feed || !('MutationObserver' in window)) return;

  function defer(root) {
    var images = root.tagName === 'IMG' ? [root] : root.querySelectorAll ? root.querySelectorAll('img') : [];

    Array.prototype.forEach.call(images, function (img) {
      if (!img.getAttribute('loading')) img.setAttribute('loading', 'lazy');
      if (!img.getAttribute('decoding')) img.setAttribute('decoding', 'async');
    });
  }

  defer(feed);

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      Array.prototype.forEach.call(mutation.addedNodes, function (node) {
        if (node.nodeType === 1) defer(node);
      });
    });
  }).observe(feed, { childList: true, subtree: true });
})();

/**
 * Collection page: "Load More" — a port of the live theme's implementation.
 *
 * Live builds this in sections/collection-template.liquid (lines 51-62 markup, 68-112
 * styles, 120-200 script). Prestige 11.4.0 has no load-more or infinite-scroll setting —
 * snippets/pagination.liquid only ever renders numbered links — so this recreates it.
 *
 * Behaviour is live's, kept deliberately identical:
 *   - a black "Load More" button under the grid;
 *   - clicking it loads the next page;
 *   - it ALSO auto-loads when the button comes within 200px of the viewport, so a shopper
 *     who just keeps scrolling never has to click (live does both — the button is not
 *     only a click target);
 *   - while fetching, the button is disabled and relabelled "Loading...", and a second
 *     grey "Loading..." line appears beneath it;
 *   - when the last page is in, the whole wrapper is hidden;
 *   - on a failed request the button says "Try Again" and stays clickable.
 *
 * Two things differ from live, because v11's markup differs:
 *   - live reads the next URL from a Liquid-rendered data-next-url on the button. v11 has
 *     no such button, so the URL is seeded from the real <nav class="pagination"> link
 *     and re-read from the fetched page's pagination each time.
 *   - the real pagination stays in the DOM and is only hidden once this script has taken
 *     over (.load-more-enabled). With no JS, no fetch, or a single page of results, the
 *     shopper keeps working numbered pagination and crawlers still see real page links.
 */
(function () {
  var READY_ATTR = 'data-load-more-ready';

  if (typeof fetch !== 'function' || typeof DOMParser !== 'function') return;

  function setup() {
    var main = document.querySelector('.collection__main');
    if (!main) return; // not a collection page

    var list = main.querySelector('.product-list');
    var nav = main.querySelector('.pagination');
    if (!list || !nav) return; // single page of results — nothing to load
    if (list.hasAttribute(READY_ATTR)) return; // already wired for this render
    list.setAttribute(READY_ATTR, '');

    var firstNext = nav.querySelector('a[rel="next"]');
    if (!firstNext) return;

    main.classList.add('load-more-enabled');

    var wrapper = document.createElement('div');
    wrapper.className = 'load-more-wrapper';
    wrapper.id = 'load-more-wrapper';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.id = 'load-more-btn';
    btn.className = 'load-more-btn';
    btn.textContent = 'Load More';
    btn.setAttribute('data-next-url', firstNext.getAttribute('href'));

    var spinner = document.createElement('div');
    spinner.className = 'load-more-spinner';
    spinner.id = 'load-more-spinner';
    spinner.textContent = 'Loading...';
    spinner.style.display = 'none';

    wrapper.appendChild(btn);
    wrapper.appendChild(spinner);
    nav.parentNode.insertBefore(wrapper, nav);

    var isLoading = false;
    var observer = null;

    function loadMore() {
      var nextUrl = btn.getAttribute('data-next-url');
      if (!nextUrl || isLoading) return;

      isLoading = true;
      btn.disabled = true;
      btn.textContent = 'Loading...';
      spinner.style.display = 'block';

      fetch(nextUrl, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newList = doc.querySelector('.collection__main .product-list');
          var nextLink = doc.querySelector('.collection__main .pagination a[rel="next"]');

          if (newList) {
            /* Snapshot before appending: .children is a live collection, and moving a node
               into this document removes it from that collection mid-iteration. Live got
               away with the same loop only because querySelectorAll returns a static list. */
            Array.prototype.slice.call(newList.children).forEach(function (card) {
              /* theme.js binds its stagger animation once, in ProductList's
                 connectedCallback (assets/theme.js:3353). Cards appended afterwards are
                 never passed to reveal(), so with settings.stagger_products_apparition on
                 — it defaults to true — they would keep the opacity: 0 that the
                 [reveal-on-scroll="true"] rule applies and never appear. theme.js:864
                 drops the attribute for the same reason. */
              card.removeAttribute('reveal-on-scroll');
              Array.prototype.forEach.call(card.querySelectorAll('[reveal-on-scroll]'), function (el) {
                el.removeAttribute('reveal-on-scroll');
              });

              card.classList.add('product-card--fade-in'); // live's .Grid__Cell--fadeIn
              list.appendChild(card);
            });
          }

          spinner.style.display = 'none';
          isLoading = false;

          if (nextLink) {
            btn.setAttribute('data-next-url', nextLink.getAttribute('href'));
            btn.disabled = false;
            btn.textContent = 'Load More';
          } else {
            wrapper.style.display = 'none';
            if (observer) observer.disconnect();
          }
        })
        .catch(function () {
          spinner.style.display = 'none';
          isLoading = false;
          btn.disabled = false;
          btn.textContent = 'Try Again';
        });
    }

    btn.addEventListener('click', loadMore);

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) loadMore();
          });
        },
        { rootMargin: '0px 0px 200px 0px' } // live's trigger distance
      );

      observer.observe(btn);
    }
  }

  setup();

  /* Faceting and sorting re-render the section, throwing away the list, the pagination and
     the button. Re-running setup() rebuilds them; it is idempotent, guarded by READY_ATTR
     on whichever .product-list is currently in the DOM. */
  var start = document.querySelector('.collection__main');
  var scope = (start && start.closest('.shopify-section')) || document.body;
  var pending = null;

  if ('MutationObserver' in window) {
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(setup, 100);
    }).observe(scope, { childList: true, subtree: true });
  }
})();

/**
 * Page popups (SIZE GUIDE, MATERIALS & WARRANTY).
 *
 * Replaces the single-instance script that used to live inside snippets/size-guide.liquid.
 * That one did `document.querySelector('[data-size-guide-overlay]')` — one hardcoded modal
 * per page — so a second popup could never work.
 *
 * This is delegated on document and pairs a trigger to its overlay by id, so any number of
 * popups can coexist and ones injected later (variant re-render, section reload) work with
 * no re-initialisation.
 *
 * Body scroll is locked while a popup is open, using the position: fixed technique so iOS
 * does not scroll the page behind it, and the scroll position is restored on close.
 */
(function () {
  var OPEN_CLASS = 'active';
  var BODY_CLASS = 'sg-open';
  var scrollPosition = 0;

  function overlayFor(id) {
    return document.querySelector('[data-page-popup-overlay="' + id + '"]');
  }

  function openPopup(overlay) {
    if (!overlay) return;

    scrollPosition = window.pageYOffset || document.documentElement.scrollTop || 0;

    overlay.classList.add(OPEN_CLASS);
    document.body.classList.add(BODY_CLASS);
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = '-' + scrollPosition + 'px';

    var close = overlay.querySelector('[data-page-popup-close]');
    if (close) close.focus();
  }

  function closePopup(overlay) {
    if (!overlay) return;

    overlay.classList.remove(OPEN_CLASS);

    if (!document.querySelector('.sg-overlay.' + OPEN_CLASS)) {
      document.body.classList.remove(BODY_CLASS);
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, scrollPosition);
    }
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-page-popup-open]');
    if (trigger) {
      event.preventDefault();
      openPopup(overlayFor(trigger.getAttribute('data-page-popup-open')));
      return;
    }

    if (event.target.closest('[data-page-popup-close]')) {
      closePopup(event.target.closest('.sg-overlay'));
      return;
    }

    /* Clicking the backdrop closes; clicking inside the panel must not. */
    var overlay = event.target.closest('.sg-overlay');
    if (overlay && !event.target.closest('[data-page-popup-panel]')) {
      closePopup(overlay);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    document.querySelectorAll('.sg-overlay.' + OPEN_CLASS).forEach(closePopup);
  });
})();

/* ---------------------------------------------------------------------------
   Cart drawer upsell strip — keep it filled after a cart change.

   theme.js's <product-recommendations> builds its fetch URL from
   extractSectionId(this.closest('.shopify-section')). <cart-drawer> is moved to
   <body> while it is open, so an element re-rendered into the OPEN drawer has no
   .shopify-section ancestor: extractSectionId(null) throws on `element.classList`
   before the fetch is issued, the element keeps the `hidden` attribute the server
   rendered it with, and the whole "Others also bought" strip disappears. Closing
   and reopening the drawer put it back inside its section, which is why it
   returned on its own.

   This repeats that fetch using the section id rendered onto the element, so it
   no longer depends on where the drawer currently sits in the DOM. Elements that
   already have children are left alone, so the theme's own successful load (on
   first open, while the drawer is still in its section) is never duplicated.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var SELECTOR = 'cart-drawer product-recommendations[data-section-id]';

  /* FRAGMENT, 1 Oct 2026 — the strip is a horizontal carousel of stock product cards
     (.cart-upsell > scroll-carousel) with no <details> toggle, open by default, inside
     .cart-drawer__items; the "collapse after an add" logic that used to live here is
     gone with the toggle. The fetch below is unchanged and is what keeps the carousel
     filled after every re-render. Adds from the cards are handled by theme.js itself
     (ProductForm for single-variant products, QuickBuyModal for sized ones). */

  function fill(element) {
    /* Guards against a second pass starting while this one is still in flight —
       the observer below fires again as soon as we touch the subtree. */
    if (element.dataset.recsLoading === '1') return;
    element.dataset.recsLoading = '1';

    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var url =
      root +
      'recommendations/products?product_id=' + encodeURIComponent(element.getAttribute('product')) +
      '&limit=' + encodeURIComponent(element.getAttribute('limit') || 4) +
      '&section_id=' + encodeURIComponent(element.getAttribute('data-section-id')) +
      '&intent=' + encodeURIComponent(element.getAttribute('intent') || 'related');

    fetch(url)
      .then(function (response) { return response.text(); })
      .then(function (html) {
        var fresh = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('product-recommendations');

        /* Nothing to recommend: leave the element hidden rather than removing it,
           so a later cart change can still fill it. */
        if (!fresh || fresh.childElementCount === 0) return;

        element.replaceChildren.apply(
          element,
          document.importNode(fresh, true).childNodes
        );
        element.hidden = false;
      })
      .catch(function () {})
      .then(function () { delete element.dataset.recsLoading; });
  }

  function refresh() {
    document.querySelectorAll(SELECTOR).forEach(function (element) {
      if (element.childElementCount === 0) fill(element);
    });
  }

  /* Coalesce a burst of mutations into one query per frame — the observer below
     watches the whole body, so refresh() must not run per mutation record. */
  var scheduled = false;

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () { /* FRAGMENT 1 Oct 2026: setTimeout, not rAF — rAF is paused in background/hidden tabs and the upsell stayed empty */
      scheduled = false;
      refresh();
    }, 0);
  }

  /* A MutationObserver rather than a timeout after 'cart:change': the drawer's
     markup is swapped in asynchronously, so there is no fixed delay that is
     correct. Watching for the empty element to appear works whatever the order.
     It watches <body> because the drawer itself is moved between <body> and its
     section wrapper, so there is no stable subtree root to observe. */
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('cart:change', schedule);
})();

/* ---------------------------------------------------------------------------
   FAQ table of contents - highlight on click only, never on scroll.

   Live's sidebar (.PageLayout__Section--secondary .FaqSummary__Link) is a plain
   set of jump links. Measured at eight scroll positions and again after a
   click: no entry ever changes class, colour, weight or underline. Clicking
   scrolls the page and does nothing else.

   Stock v11 ships <faq-toc>, an IntersectionObserver scrollspy (theme.js,
   rootMargin "0px 0px -70% 0px") that slides the underline from one category to
   the next as the page scrolls. That is the behaviour being removed here.

   The observer is private state inside the custom element, so it cannot be
   switched off from the outside. Swapping the <faq-toc> tag for a plain <div>
   takes the element out of the document, which fires its disconnectedCallback
   and disconnects the observer for good. Every style hangs off the .faq__toc /
   .faq__toc-item classes, which the <div> keeps, and no CSS rule anywhere
   selects the faq-toc element itself - so nothing moves visually.

   What replaces it: the entry you click becomes the active one. The first entry
   starts active, which is what faq.liquid already renders.

   This also retires the earlier "last entry once the document is scrolled to
   the bottom" tail fix. That existed only to patch a corner case of the
   scrollspy, and with the scrollspy gone it would be the one remaining thing
   moving the underline on scroll.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var ACTIVE = 'is-active';

  function retireScrollspy() {
    document.querySelectorAll('faq-toc').forEach(function (toc) {
      var plain = document.createElement('div');
      plain.className = toc.className;
      while (toc.firstChild) plain.appendChild(toc.firstChild);
      toc.parentNode.replaceChild(plain, toc);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', retireScrollspy);
  } else {
    retireScrollspy();
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('.faq__toc a[href^="#"]');
    if (!link) return;

    var links = link.parentNode.querySelectorAll('a[href^="#"]');
    Array.prototype.forEach.call(links, function (other) {
      other.classList.toggle(ACTIVE, other === link);
    });
  });
})();

/* ---------------------------------------------------------------------------
   Product sticky add-to-cart bar — show it BEFORE the fold too.

   theme.js's ProductStickyBar reveals the bar only when

       !formEntry.isIntersecting && formEntry.boundingClientRect.bottom < 0

   i.e. the add-to-cart form must have scrolled ABOVE the viewport. Live's rule,
   stated in its own snippets/sticky-add-to-cart.liquid header, is broader:

       "Shown once the real button scrolls out of view, whether the user hasn't
        reached it yet (still scrolling down through images) or has scrolled
        past it. Hidden again once the footer comes into view."

   So on a long mobile gallery, live shows the bar the whole way down while the
   real button is still below the fold; the draft showed nothing until you had
   scrolled past it.

   Rather than edit the compiled theme.js, this adds a second observer that sets
   its own `is-visible-prefold` class, which assets/custom.css makes visible the
   same way theme.css makes `.is-visible` visible. The two classes are
   independent, so theme.js keeps toggling its own and neither fights the other.
   The footer condition is kept identical to theme.js's, so the bar still gets
   out of the way at the bottom of the page.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var VISIBLE = 'is-visible-prefold';

  function init() {
    document.querySelectorAll('product-sticky-bar').forEach(function (bar) {
      if (bar.dataset.prefoldReady === '1') return;

      var form = document.forms[bar.getAttribute('form')];
      var footer = document.querySelector('.shopify-section--footer');
      if (!form || !footer) return;

      bar.dataset.prefoldReady = '1';

      /* Mirrors theme.js's two conditions, except that the form one is not
         restricted to "above the viewport" — out of view in either direction
         counts, which is the whole point of this block. */
      var formOut = false;
      var footerOut = false;

      function apply() {
        bar.classList.toggle(VISIBLE, formOut && footerOut);
      }

      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.target === form) formOut = !entry.isIntersecting;
          if (entry.target === footer) footerOut = !entry.isIntersecting;
        });
        apply();
      }).observe(form);

      /* Two observers rather than one: observing both targets through a single
         callback works, but the form and the footer are re-observed
         independently when product-rerender swaps the form on a variant change. */
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          footerOut = !entry.isIntersecting;
        });
        apply();
      }).observe(footer);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The bar lives inside <product-rerender>, so a variant change replaces the
     element and the new one needs wiring. */
  new MutationObserver(init).observe(document.body, { childList: true, subtree: true });
})();

/* ---------------------------------------------------------------------------
   Desktop menu drawer — pin the drawer to the header's bottom edge.

   Publishes --fragment-menu-top, which assets/custom.css uses for
   .header-sidebar::part(base) { inset-block-start }.

   WHY JS IS NEEDED AT ALL. The correct offset is the header's bottom edge in
   viewport coordinates. Unscrolled, the non-sticky announcement bar sits above
   the header, so that edge is announcement_height + header_height; once scrolled
   past it the sticky header is at y=0 and the edge is just header_height. The
   value is therefore max(0, announcement_height - scrollY) + header_height,
   and CSS cannot read scroll position. The theme's own --sticky-area-height
   omits the announcement term entirely (its announcement bar is configured
   non-sticky), which is what made the drawer cover SHOP at the top of the page.

   Measuring beats recomputing from variables: getBoundingClientRect().bottom is
   already the answer for every case — announcement bar present, absent, dismissed,
   half-scrolled, wrapped onto two lines at a narrow width, transparent header —
   with no formula to keep in sync with the theme's settings.

   Listeners are attached ONLY while the drawer is open, and scroll is passive
   and rAF-coalesced, so there is no cost on a normal page.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var PROP = '--fragment-menu-top';
  var drawer = document.getElementById('sidebar-menu');
  if (!drawer) return;

  /* The section wrapper, not .header: the wrapper is the element the theme
     transforms for its hide-on-scroll behaviour, so its rect already reflects
     any translate that is applied. Falls back to the inner header. */
  var header =
    document.querySelector('.shopify-section--header') ||
    document.querySelector('.header');
  if (!header) return;

  var frame = null;

  function measure() {
    frame = null;
    /* Clamp at 0: if the header is scrolled fully out of view (hide-on-scroll),
       a negative bottom would pull the drawer off the top of the screen. */
    var top = Math.max(0, header.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty(PROP, top.toFixed(2) + 'px');
  }

  function schedule() {
    if (frame === null) frame = requestAnimationFrame(measure);
  }

  var listening = false;

  function startTracking() {
    measure();
    if (listening) return;
    listening = true;
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
  }

  function stopTracking() {
    if (!listening) return;
    listening = false;
    window.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
    if (frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
    /* The property is deliberately NOT cleared. Leaving the last good value in
       place means the next open paints at the right offset on the very first
       frame, before the observer runs — otherwise the drawer would start from
       the CSS fallback and visibly jump into position. */
  }

  /* theme.js marks the open state with an `open` attribute on the host
     (setAttribute("open", ...) / removeAttribute("open")), so this needs no
     hook into the drawer's own open/close methods. */
  function sync() {
    if (drawer.hasAttribute('open')) startTracking();
    else stopTracking();
  }

  new MutationObserver(sync).observe(drawer, {
    attributes: true,
    attributeFilter: ['open'],
  });

  sync();
})();

/* ---------------------------------------------------------------------------
   Desktop menu drawer — never close on an outside click.

   ROOT CAUSE. DialogElement runs a focus trap whose clickOutsideDeactivates
   callback fires on every click outside the trapped element
   (theme.js:2096 allowOutsideClickMouse_fn):

       if (this.hideForOutsideClickTarget(event.target)) { this.hide(); }

   and the default test (theme.js:2047) is simply

       hideForOutsideClickTarget(target) { return !this.contains(target); }

   WHY PAGE CLICKS ALREADY SEEMED FIXED, BUT HEADER CLICKS DID NOT. This is not
   two different bugs. The drawer's ::part(base) is position:fixed and, since the
   earlier fix, spans from the header's bottom edge down — so it covers the whole
   page area. A click "on the page" actually lands on that part; shadow DOM
   retargets it to the host, this.contains(host) is true, and nothing happens.
   The header and the announcement bar sit ABOVE the base's top offset, so a
   click there hits real header elements, which genuinely are outside the drawer
   — the test returns true and hide() runs. Removing the overlay last time never
   addressed this path.

   THE FIX. hideForOutsideClickTarget is a public method that the theme's own
   comments describe as the control point for exactly this, so it is overridden
   on the single #sidebar-menu instance. No core theme.js edit, and because it is
   an own property on one element, every other dialog — cart drawer, search,
   facets, quick buy, size guide — keeps the stock behaviour untouched.

   WHAT STILL CLOSES IT: the X button (dialog-close-button calls hide()
   directly) and Esc (handled separately by the dialog's key listener). Neither
   goes through this method.

   whenDefined, because custom.js and theme.js are both deferred with no
   guaranteed order; waiting for the class means the element is upgraded before
   the own property is set, so the prototype method cannot shadow it.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  customElements.whenDefined('header-sidebar').then(function () {
    var drawer = document.getElementById('sidebar-menu');
    if (!drawer) return;

    /* Always "inside": no outside click ever triggers hide(). The argument is
       ignored deliberately — the requirement is that nothing outside closes the
       menu, including the header and the announcement bar. */
    drawer.hideForOutsideClickTarget = function () {
      return false;
    };
  });
})();

/* ---------------------------------------------------------------------------
   Sticky add-to-cart bar — stack above the Pandectes cookie banner.

   THE PROBLEM. Both are position:fixed and bottom-anchored, so they occupy the
   same strip. Measured at scroll 0 with pb=0:

     768x1024  bar    y=912 h=112 z-index 3      banner y=918 h=106  overlap 106px
     390x844   bar    y=776 h=68  z-index 3      banner y=616 h=228  overlap 68px

   The banner's z-index is 2147483647, so it wins outright and the bar is
   invisible underneath it until the banner is dismissed. The bar's own
   show/hide logic is correct and is NOT touched here.

   Note the banner is 106px at 768 but 228px at 390 — its height changes with
   viewport and copy length, so the offset is read from the DOM every time
   rather than hardcoded.

   THREE THINGS VERIFIED AGAINST THE LIVE MARKUP, because the obvious
   assumptions are all wrong for this app:

   1. DISMISSAL DOES NOT SET display:none, AND DOES NOT REMOVE THE NODE.
      Pandectes adds a `cc-invisible` class and collapses the height; computed
      display stays `flex` and the element stays in the DOM. So a
      `display !== 'none'` test never detects dismissal. offsetHeight is the
      only reliable signal, and it is what this uses.

   2. THE BUTTON CLASSES ARE `.cc-btn.cc-btn-decision.cc-allow` (ACCEPT) and
      `.cc-btn.cc-settings` (Preferences). There is no `.cc-dismiss` and no
      `.cc-deny` in this install. `.cc-btn` covers both real buttons, and
      Preferences is worth catching too since opening it resizes the banner.

   3. A FIXED 300ms DELAY IS NOT SAFE. Measured after the accept click, the
      banner height was still 106px at +150ms and only reached 0 at +300ms —
      i.e. a single 300ms timeout lands exactly on the boundary. A ResizeObserver
      on the banner reacts to the real collapse instead of guessing, so the
      timeout is kept only as a belt-and-braces fallback for browsers that miss
      the observer.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  /* FRAGMENT, 16 Oct 2026 (V12): Pandectes renders the bar with `cc-banner` on
     desktop/tablet but `cc-floating` on phones (<600px). The old selector only
     matched `cc-banner`, so on phones the offset stayed 0 and the sticky bar sat
     UNDER the banner (z-index 2147483647) — invisible. Match the window itself. */
  var BANNER_SELECTOR = '.pd-cookie-banner-window.cc-window';
  var BAR_SELECTOR = '.product-sticky-bar';

  function bannerOffset() {
    var banner = document.querySelector(BANNER_SELECTOR);
    if (!banner) return 0;
    /* offsetHeight, not display: see note 1 above. It is 0 once Pandectes
       collapses the banner, which is exactly the signal we want. */
    return banner.offsetHeight > 0 ? banner.offsetHeight : 0;
  }

  function adjustStickyBarForCookieBanner() {
    var offset = bannerOffset();
    /* FRAGMENT, 16 Oct 2026 (V12): the same offset is published as a CSS variable so
       custom.css can lift the cart drawer's footer ("Passer la commande") above the
       banner too — it used to sit under it on phones. */
    document.documentElement.style.setProperty('--fs-cookie-banner-offset', offset + 'px');
    var bar = document.querySelector(BAR_SELECTOR);
    if (!bar) return;
    bar.style.bottom = offset + 'px';
  }

  /* Re-run on the events that can change either box. */
  window.addEventListener('load', adjustStickyBarForCookieBanner);
  window.addEventListener('resize', adjustStickyBarForCookieBanner, { passive: true });
  window.addEventListener('orientationchange', adjustStickyBarForCookieBanner);

  /* The banner is injected by the app after page load, and the bar is replaced
     by <product-rerender> on a variant change, so neither is guaranteed to
     exist when this runs. Watch for both, and attach the ResizeObserver to the
     banner as soon as it appears. */
  var resizeObserver =
    typeof ResizeObserver === 'function' ? new ResizeObserver(adjustStickyBarForCookieBanner) : null;
  var observedBanner = null;

  function wire() {
    var banner = document.querySelector(BANNER_SELECTOR);
    if (resizeObserver && banner && banner !== observedBanner) {
      if (observedBanner) resizeObserver.unobserve(observedBanner);
      resizeObserver.observe(banner);
      observedBanner = banner;
    }
    adjustStickyBarForCookieBanner();
  }

  new MutationObserver(wire).observe(document.body, { childList: true, subtree: true });

  /* Fallback for the no-ResizeObserver case. Two delays rather than one,
     because 300ms is the measured boundary and a single shot at it is a
     coin flip — see note 3. */
  document.addEventListener('click', function (event) {
    if (event.target.closest && event.target.closest('.cc-btn')) {
      setTimeout(adjustStickyBarForCookieBanner, 350);
      setTimeout(adjustStickyBarForCookieBanner, 900);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire);
  } else {
    wire();
  }
})();

/* HERO SLIDESHOW — cross-fade (FRAGMENT, 1 Oct 2026, test theme).
   Stock Prestige 11 fades the outgoing slide out, hides it, then fades the
   incoming one in from opacity 0 over the slideshow background — which the
   theme settings leave transparent, so every change shows a white screen for
   ~0.8 s even though all slide images are preloaded. Here the outgoing slide
   stays painted as an overlay and fades out over the already-visible new
   slide, so there is never an empty frame. Videos keep the stock mute/play
   handling; "instant" selections finish the animation at once. */
(function(){"use strict";
  if(!("animate" in Element.prototype)||!window.customElements)return;
  customElements.whenDefined("slideshow-carousel").then(function(){
    var Carousel=customElements.get("slideshow-carousel");if(!Carousel||Carousel.prototype.__fragmentCrossfade)return;
    Carousel.prototype.__fragmentCrossfade=true;
    Carousel.prototype.createOnChangeAnimationControls=function(fromSlide,toSlide){
      Array.prototype.forEach.call(fromSlide.querySelectorAll("video"),function(v){v.muted=true;v.pause();});
      Array.prototype.forEach.call(toSlide.querySelectorAll("video"),function(v){if(v.offsetParent){v.muted=true;v.currentTime=0;v.play();}});
      /* keep the outgoing slide visible as an overlay while it loses .is-selected */
      fromSlide.style.position="absolute";fromSlide.style.inset="0";fromSlide.style.zIndex="5";
      fromSlide.style.visibility="visible";fromSlide.style.pointerEvents="none";
      var easing="cubic-bezier(.25,.46,.45,.94)";
      var fade=fromSlide.animate([{opacity:1},{opacity:0}],{duration:650,easing:easing,fill:"forwards"});
      var content=toSlide.querySelector(".slideshow__slide-content");
      var rise=content?content.animate([{opacity:0,transform:"translateY(20px)"},{opacity:1,transform:"translateY(0)"}],{duration:600,delay:150,easing:"cubic-bezier(.215,.61,.355,1)",fill:"backwards"}):null;
      function cleanup(){
        ["position","inset","z-index","visibility","pointer-events","opacity"].forEach(function(prop){fromSlide.style.removeProperty(prop);});
        try{fade.cancel();}catch(e){}
      }
      var finished=Promise.all([fade.finished,rise?rise.finished:Promise.resolve()]).catch(function(){}).then(cleanup);
      return {finished:finished,finish:function(){fade.finish();if(rise)rise.finish();}};
    };
  });
})();

/* NO PRESELECTED SIZE (FRAGMENT, 1 Oct 2026, V3 test).
   Oscar: no size preselected. On product pages whose Size option has more than
   one value, the size radios start unchecked, both add-to-cart buttons (form and
   sticky bar) read "Choose a size" and a tap on them scrolls to the picker
   instead of adding the first variant. Picking a size restores the normal
   button. Untouched: products without a Size option or with a single size, and
   landings that already carry ?variant= (ads deep-link to a specific size). */
(function(){"use strict";
  if(/[?&]variant=/.test(location.search))return;
  var LABELS={fr:["CHOISIR UNE TAILLE","Choisissez une taille pour continuer"],en:["SELECT A SIZE","Choose a size to continue"],de:["GRÖSSE WÄHLEN","Bitte wähle eine Größe"],es:["ELIGE UNA TALLA","Elige una talla para continuar"],it:["SCEGLI UNA TAGLIA","Scegli una taglia per continuare"],ja:["サイズを選択","サイズをお選びください"]};
  var SIZE_RE=/^(size|taille|größe|groesse|talla|taglia|サイズ)\s*:?$/i;
  function labels(){var l=(document.documentElement.lang||"en").slice(0,2).toLowerCase();return LABELS[l]||LABELS.en;}
  var gatedModal={};
  /* the theme re-injects the quick-buy modal form after each choice: gate it once per opening, never a re-render */
  document.addEventListener("dialog:after-hide",function(e){if(e.target&&e.target.matches&&e.target.matches("quick-buy-modal")){gatedModal={};}},true);
  function setup(form){
    if(!form||form.dataset.sizeGate)return;
    if(form.closest("quick-buy-modal")){var fid=form.getAttribute("id");if(gatedModal[fid])return;gatedModal[fid]=true;}
    /* the id property is shadowed by the hidden <input name="id"> — read the attribute */
    var formId=form.getAttribute("id");
    var picker=document.querySelector('variant-picker[form-id="'+formId+'"]');
    if(!picker)return;
    var fieldset=null;
    Array.prototype.forEach.call(picker.querySelectorAll("fieldset"),function(fs){var lg=fs.querySelector("legend");if(!fieldset&&lg&&SIZE_RE.test(lg.textContent.trim()))fieldset=fs;});
    if(!fieldset)return;
    var radios=Array.prototype.slice.call(fieldset.querySelectorAll('input[type="radio"][data-option-position]'));
    if(radios.length<2)return;
    /* FRAGMENT, 3 Oct 2026 (V7): a fully sold-out product keeps the theme's own
       "Épuisé"/"Sold out" button — never gate it. Sold out = every size value is
       flagged is-disabled by the theme, or the server rendered every add-to-cart
       button disabled (no available variant at all). */
    var allSizesOut=radios.every(function(r){var lb=document.querySelector('label[for="'+r.id+'"]');return r.disabled||(lb&&lb.classList.contains("is-disabled"));});
    var btnsNow=Array.prototype.slice.call(document.querySelectorAll('buy-buttons[form="'+formId+'"] button[type="submit"]'));
    var allBtnsOff=btnsNow.length>0&&btnsNow.every(function(b){return b.disabled||b.getAttribute("aria-disabled")==="true";});
    if(allSizesOut||allBtnsOff){form.dataset.sizeGate="soldout";return;}
    form.dataset.sizeGate="pending";
    radios.forEach(function(r){r.checked=false;});
    var L=labels();
    var hint=document.createElement("p");hint.className="size-gate__hint";hint.hidden=true;hint.textContent=L[1];fieldset.appendChild(hint);
    function buttons(){return Array.prototype.slice.call(document.querySelectorAll('buy-buttons[form="'+formId+'"] button[type="submit"]'));}
    function apply(){
      var pending=form.dataset.sizeGate==="pending";
      buttons().forEach(function(b){
        if(pending){
          if(!b.dataset.sizeGateLabel){b.dataset.sizeGateLabel=b.innerHTML;}
          var span=b.querySelector("span");
          if(span){if(span.textContent!==L[0])span.textContent=L[0];}else if(b.textContent!==L[0]){b.textContent=L[0];}
          b.classList.add("size-gate__button");
        }else if(b.dataset.sizeGateLabel){
          b.innerHTML=b.dataset.sizeGateLabel;delete b.dataset.sizeGateLabel;b.classList.remove("size-gate__button");
        }
      });
    }
    apply();
    document.addEventListener("click",function(e){
      if(form.dataset.sizeGate!=="pending"||!document.contains(form))return;
      var b=e.target.closest&&e.target.closest('buy-buttons[form="'+formId+'"] button[type="submit"]');
      if(!b)return;
      e.preventDefault();e.stopImmediatePropagation();
      hint.hidden=false;fieldset.classList.add("size-gate__fieldset--attention");
      fieldset.scrollIntoView({behavior:"smooth",block:"center"});
      setTimeout(function(){fieldset.classList.remove("size-gate__fieldset--attention");},1500);
    },true);
    fieldset.addEventListener("change",function(e){
      if(e.target&&e.target.matches('input[type="radio"]')&&e.target.checked){form.dataset.sizeGate="done";hint.hidden=true;apply();}
    });
    /* the theme re-renders the sticky bar; keep the label while still pending */
    var scheduled=false;
    var formObserver=new MutationObserver(function(){
      if(!document.contains(form)){formObserver.disconnect();return;}
      if(form.dataset.sizeGate!=="pending"||scheduled)return;
      scheduled=true;setTimeout(function(){scheduled=false;apply();},0);
    });formObserver.observe(document.body,{childList:true,subtree:true});
  }
  function init(){Array.prototype.forEach.call(document.querySelectorAll('form[action*="/cart/add"][id]'),setup);}
  document.readyState==="loading"?document.addEventListener("DOMContentLoaded",init):init();
  /* FRAGMENT, 1 Oct 2026: forms injected later (the theme's quick-buy modal in the cart upsell) get the same rule — no preselected size there either */
  if("MutationObserver" in window){var pendingInit=false;new MutationObserver(function(){if(pendingInit)return;pendingInit=true;setTimeout(function(){pendingInit=false;init();},50);}).observe(document.documentElement,{childList:true,subtree:true});}
})();

/* ---------------------------------------------------------------------------
   FRAGMENT, 1 Oct 2026, cart size selector (test).

   Lets a customer change the size of a line item from the cart drawer. The
   <select class="line-item-size"> is rendered by snippets/line-item.liquid; every
   <option> value is the id of the variant that has that size and the item's
   other options unchanged.

   Shopify has no "change variant" endpoint, so a swap is two calls. The new
   variant is ADDED first (quantity, properties and selling plan copied from the
   live cart JSON), then the old line is removed with cart/change.js — in that
   order a failed add (sold out between render and click) leaves the cart
   untouched. The remove call carries the `sections` the theme's own
   LineItemQuantity bundles (cart:prepare-bundled-sections), and its response —
   the cart JSON with those sections rendered — is dispatched on the very same
   "cart:change" event the theme uses after a quantity change
   ({ baseEvent: 'line-item:change', cart }), so the drawer, cart count, cart dot
   and free-shipping bar all update through their own listeners. Nothing in
   theme.js listens to line-item:will-change / line-item:change, so those are
   not re-dispatched. Every URL goes through Shopify.routes.root (market prefix).

   The swapped item re-renders at the TOP of the drawer: cart/add.js always
   inserts a new line first, and Shopify offers no way to keep the position.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var SELECTOR = 'select.line-item-size';

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function emit(name, detail) {
    document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
  }

  /* Same handshake as theme.js: the cart drawer pushes its section id into
     detail.sections, and the joined list goes out as the `sections` parameter. */
  function bundledSections() {
    var sections = [];
    emit('cart:prepare-bundled-sections', { sections: sections });
    return sections.join(',');
  }

  function postJson(path, body) {
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (json) {
        if (!response.ok) {
          throw new Error(json.description || json.message || (path + ' ' + response.status));
        }
        return json;
      });
    });
  }

  function showError(select, message) {
    var field = select.closest('.line-item-size-field') || select;
    var previous = field.parentNode.querySelector('.line-item-size__error');
    if (previous) previous.remove();
    var alert = document.createElement('p');
    alert.className = 'line-item-size__error text-xs';
    alert.setAttribute('role', 'alert');
    alert.textContent = message;
    field.insertAdjacentElement('afterend', alert);
  }

  function isCartPage() {
    return !!(window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart');
  }

  function swap(select) {
    var lineKey = select.getAttribute('data-line-key');
    var previousId = select.getAttribute('data-current-variant');
    var newId = parseInt(select.value, 10);

    if (!lineKey || !newId || String(newId) === previousId || select.disabled) return;

    select.disabled = true;
    select.setAttribute('aria-busy', 'true');
    emit('theme:loading:start');

    /* Set once the new variant is in the cart: past that point an error must NOT
       restore the select as if nothing happened — the drawer is re-synced from the
       server instead so it shows the real state (both lines). */
    var added = false;

    fetch(root() + 'cart.js')
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var item = (cart.items || []).filter(function (lineItem) { return lineItem.key === lineKey; })[0];
        if (!item) throw new Error('line ' + lineKey + ' is no longer in the cart');

        var add = { id: newId, quantity: item.quantity };
        if (item.properties && Object.keys(item.properties).length) add.properties = item.properties;
        if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
          add.selling_plan = item.selling_plan_allocation.selling_plan.id;
        }
        return postJson('cart/add.js', add);
      })
      .then(function () {
        added = true;
        return postJson('cart/change.js', { id: lineKey, quantity: 0, sections: bundledSections() });
      })
      .then(function (cartContent) {
        emit('theme:loading:end');
        if (isCartPage()) {
          window.location.reload();
          return;
        }
        emit('cart:change', { baseEvent: 'line-item:change', cart: cartContent });
      })
      .catch(function (error) {
        emit('theme:loading:end');
        console.warn('[cart size selector] size change failed' + (added ? ' after the new size was added' : '') + ':', error);
        if (added) {
          /* The theme's own full re-render from the server (CartDrawer.refreshCart_fn,
             CartCount/CartDot updateFromServer). */
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          return;
        }
        select.value = previousId;
        select.disabled = false;
        select.removeAttribute('aria-busy');
        showError(select, error && error.message ? error.message : 'Error');
      });
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (target && target.matches && target.matches(SELECTOR)) swap(target);
  });
})();

/* ---------------------------------------------------------------------------
   FRAGMENT, 4 Oct 2026 (V7) — WHEN the email popup opens.
   Oscar: rework only the moment the "JOIN THE FAM" popup (Klaviyo form VUAvaC)
   is shown. Klaviyo used to open it on the first of 15 s / 30 % scroll / exit
   intent — on mobile 30 % scroll happens within seconds of landing, on top of
   the cookie banner (Klaviyo, last 30 days: 65,906 views, 0.8 % sign-up).
   This script opens the same form itself, only when:
     · the cookie choice has been made (Pandectes banner gone), and
     · the page is not the home page / cart / account / policies / checkout, and
     · no cart drawer or quick-buy window is open (and 5 s have passed since
       the cookie choice or since such a window closed), and
     · ONE of: product page with ≥25 s on page and ≥50 % scrolled;
               2nd page of the visit with ≥10 s on page;
               desktop exit intent (pointer leaves the top) after ≥8 s;
               fallback ≥40 s on any eligible page.
   Never for: logged-in customers, profiles Klaviyo already knows, people who
   signed up from it, or within 14 days of the last time it was shown.
   At most once per visit. REQUIRES, once V7 is live: in Klaviyo, set the form's
   display timing to "custom trigger" so Klaviyo's own timers stop.
   Debug: add ?popup_debug=1 to log each decision in the console.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';
  var FORM_ID = 'VUAvaC';
  var COOLDOWN_DAYS = 14;
  var KEY_SHOWN = 'fs_popup_shown_at', KEY_DONE = 'fs_popup_signed_up', KEY_VIEWS = 'fs_popup_pageviews', KEY_SESSION = 'fs_popup_session_shown';
  var debug = /[?&]popup_debug=1/.test(location.search);
  function log(m) { if (debug) console.log('[fs-popup] ' + m); }
  function ls(k, v) { try { if (v === undefined) return localStorage.getItem(k); localStorage.setItem(k, v); } catch (e) { return null; } }
  function ss(k, v) { try { if (v === undefined) return sessionStorage.getItem(k); sessionStorage.setItem(k, v); } catch (e) { return null; } }

  var path = location.pathname.toLowerCase().replace(/\/+$/, '');
  var localeless = path.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/, '');
  var isHome = localeless === '' ;
  var excluded = isHome || /^\/(cart|account|checkouts?|policies|pages\/(contact|cgv|terms|privacy|mentions)|search|password|challenge)/.test(localeless);
  var isProduct = /^\/(collections\/[^/]+\/)?products\//.test(localeless);

  /* page-view counter for this visit (all pages count, home included) */
  var views = (parseInt(ss(KEY_VIEWS) || '0', 10) || 0) + 1; ss(KEY_VIEWS, String(views));

  /* Klaviyo form events: remember sign-ups for good, shows for the cool-down */
  window.addEventListener('klaviyoForms', function (e) {
    var d = e && e.detail; if (!d || d.formId !== FORM_ID) return;
    if (d.type === 'submit' || d.type === 'stepSubmit') ls(KEY_DONE, '1');
    if (d.type === 'open') { ls(KEY_SHOWN, String(Date.now())); ss(KEY_SESSION, '1'); }
  });

  function blockedForever() {
    if (ls(KEY_DONE) === '1') return 'already signed up';
    var m = window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.page;
    if (m && m.customerId) return 'logged-in customer';
    var last = parseInt(ls(KEY_SHOWN) || '0', 10);
    if (last && Date.now() - last < COOLDOWN_DAYS * 864e5) return 'shown less than ' + COOLDOWN_DAYS + ' days ago';
    if (ss(KEY_SESSION) === '1') return 'already shown this visit';
    return null;
  }
  var reason = excluded ? 'excluded page' : blockedForever();
  if (reason) { log('off: ' + reason); return; }

  var start = Date.now(), maxScroll = 0, exitIntent = false, opened = false, calmSince = 0;
  function scrolled() {
    var h = document.documentElement.scrollHeight - innerHeight;
    var p = h > 0 ? (scrollY / h) * 100 : 100; if (p > maxScroll) maxScroll = p;
  }
  addEventListener('scroll', scrolled, { passive: true });
  if (matchMedia('(pointer: fine)').matches) {
    document.addEventListener('mouseout', function (e) { if (!e.relatedTarget && e.clientY <= 0) exitIntent = true; });
  }
  function cookieBannerOpen() {
    var b = document.querySelector('.pd-cookie-banner-window');
    if (!b) return false;
    var cs = getComputedStyle(b), r = b.getBoundingClientRect();
    return cs.display !== 'none' && cs.visibility !== 'hidden' && +cs.opacity > 0.05 && r.height > 0 && !b.classList.contains('cc-invisible');
  }
  function overlayOpen() {
    return !!document.querySelector('cart-drawer[open], quick-buy-modal[open], .size-chart-drawer[open], x-modal[open]');
  }
  function klaviyoKnows(cb) {
    try {
      if (window.klaviyo && typeof klaviyo.isIdentified === 'function') {
        Promise.resolve(klaviyo.isIdentified()).then(function (v) { cb(!!v); }, function () { cb(false); });
        return;
      }
    } catch (e) {}
    cb(false);
  }
  function open(why) {
    if (opened) return; opened = true;
    klaviyoKnows(function (known) {
      if (known) { log('skip: Klaviyo already knows this visitor'); return; }
      log('open: ' + why);
      ls(KEY_SHOWN, String(Date.now())); ss(KEY_SESSION, '1');
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', FORM_ID]);
    });
  }
  var timer = setInterval(function () {
    if (opened) return clearInterval(timer);
    if (document.visibilityState === 'hidden') { start += 1000; return; } /* count only active time */
    scrolled();
    var secs = (Date.now() - start) / 1000;
    if (cookieBannerOpen() || overlayOpen()) { calmSince = 0; return; }
    if (!calmSince) calmSince = Date.now();
    if (Date.now() - calmSince < 5000) return; /* never right after the cookie choice or a drawer closes */
    if (isProduct && secs >= 25 && maxScroll >= 50) return open('product page, ' + Math.round(secs) + ' s, ' + Math.round(maxScroll) + ' % scrolled');
    if (views >= 2 && secs >= 10) return open('page ' + views + ' of the visit, ' + Math.round(secs) + ' s');
    if (exitIntent && secs >= 8) return open('exit intent after ' + Math.round(secs) + ' s');
    if (secs >= 40) return open('fallback ' + Math.round(secs) + ' s');
  }, 1000);
  log('armed: views=' + views + ' product=' + isProduct);
  window.__fsPopup = { formId: FORM_ID, state: function () { return { views: views, secs: Math.round((Date.now() - start) / 1000), maxScroll: Math.round(maxScroll), exitIntent: exitIntent, cookieBanner: cookieBannerOpen(), overlay: overlayOpen(), opened: opened }; } };
})();
